import React, { useEffect, useState } from 'react';
import api from '../api';
import { toDateTimeLocalValue } from '../utils/localDateTime';
import Papa from 'papaparse';
import { format } from "date-fns";
import {
  Box, Typography, Card, CardContent, CardActions, Button, Grid, Chip, IconButton, Snackbar, Tooltip, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import ReplayIcon from '@mui/icons-material/ReplayOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import ConversationModal from './ConversationModal';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const SentMessages = ({ propertyId, unitId }) => {
  const [messages, setMessages] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [conversationOpen, setConversationOpen] = useState(false);
  const [conversationTenant, setConversationTenant] = useState(null);
  const [conversationPropertyId, setConversationPropertyId] = useState(null);
  const [conversationUnitId, setConversationUnitId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMessage, setEditMessage] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editScheduledAt, setEditScheduledAt] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [propertyId, unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Determine which endpoint to use
      const messagesEndpoint = propertyId && unitId 
        ? `/api/properties/${propertyId}/units/${unitId}/messages`
        : `/api/messages`; // Fetch ALL messages if no property/unit filter
      
      console.log('🔍 Fetching messages from:', messagesEndpoint);
      
      const [messagesRes, tenantsRes] = await Promise.all([
        api.get(messagesEndpoint),
        api.get('/api/tenants')
      ]);
      
      console.log('📊 Received messages:', messagesRes.data.length);
      
      // Mock status if not present
      const withStatus = messagesRes.data.map(msg => ({ ...msg, status: msg.status || 'sent' }));
      setMessages(withStatus);
      setTenants(tenantsRes.data);
    } catch (err) {
      console.error('❌ Error fetching messages:', err);
      setSnackbar({ open: true, message: 'Failed to fetch data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getTenantNameByEmail = (email) => {
    const tenant = tenants.find(t => t.email === email);
    return tenant ? tenant.name : email;
  };

  const exportToCSV = () => {
    const csvData = messages.map((msg) => ({
      Subject: msg.subject,
      RecipientEmail: msg.recipientId,
      RecipientName: getTenantNameByEmail(msg.recipientId),
      SentBy: msg.sentBy,
      Status: msg.status,
      SentAt: msg.createdAt
        ? format(
            typeof msg.createdAt.toDate === 'function'
              ? msg.createdAt.toDate()
              : new Date(msg.createdAt._seconds ? msg.createdAt._seconds * 1000 : msg.createdAt),
            "Pp"
          )
        : '',
      Message: msg.body
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'sent_messages.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSnackbar({ open: true, message: 'Exported to CSV', severity: 'success' });
  };

  const handleDelete = async (msg) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      // Use message's propertyId/unitId if available, otherwise use current selection
      const propId = msg.propertyId || propertyId;
      const uId = msg.unitId || unitId;
      
      if (!propId || !uId) {
        setSnackbar({ open: true, message: 'Cannot delete: missing property/unit information', severity: 'error' });
        return;
      }
      
      await api.delete(`/api/properties/${propId}/units/${uId}/messages/${msg.id}`);
      setSnackbar({ open: true, message: 'Message deleted successfully!', severity: 'success' });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete message', severity: 'error' });
    }
  };

  const handleEdit = (msg) => {
    setEditMessage(msg);
    setEditSubject(msg.subject || '');
    setEditBody(msg.body || '');
    setEditScheduledAt(toDateTimeLocalValue(msg.scheduledAt));
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editMessage) return;
    setEditLoading(true);
    try {
      const propId = editMessage.propertyId || propertyId;
      const uId = editMessage.unitId || unitId;
      
      await api.put(`/api/properties/${propId}/units/${uId}/messages/${editMessage.id}`, {
        subject: editSubject,
        body: editBody,
        scheduledAt: editScheduledAt ? new Date(editScheduledAt).toISOString() : undefined,
      });
      setSnackbar({ open: true, message: 'Message updated successfully!', severity: 'success' });
      setEditDialogOpen(false);
      setEditMessage(null);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update message', severity: 'error' });
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancel = async (msg) => {
    try {
      const propId = msg.propertyId || propertyId;
      const uId = msg.unitId || unitId;
      
      await api.delete(`/api/properties/${propId}/units/${uId}/messages/${msg.id}`);
      setSnackbar({ open: true, message: 'Scheduled message canceled!', severity: 'success' });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to cancel message', severity: 'error' });
    }
  };

  const handleRetry = async (msg) => {
    try {
      const propId = msg.propertyId || propertyId;
      const uId = msg.unitId || unitId;
      
      await api.post(`/api/properties/${propId}/units/${uId}/messages/${msg.id}/retry`);
      setSnackbar({ open: true, message: 'Retry triggered!', severity: 'success' });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to retry message', severity: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'primary';
      case 'delivered': return 'success';
      case 'failed': return 'error';
      case 'scheduled': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircleIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'scheduled': return <ScheduleIcon color="warning" />;
      default: return <SendIcon color="primary" />;
    }
  };

  // Filter and search logic
  const filteredMessages = messages.filter(msg => {
    const matchesStatus = statusFilter === 'all' || msg.status === statusFilter;
    const searchLower = search.toLowerCase();
    const matchesSearch =
      msg.subject?.toLowerCase().includes(searchLower) ||
      msg.body?.toLowerCase().includes(searchLower) ||
      (msg.recipients && msg.recipients.join(', ').toLowerCase().includes(searchLower));
    return matchesStatus && matchesSearch;
  });

  // Tabs logic
  const tabMessages =
    activeTab === 0
      ? filteredMessages
      : activeTab === 1
      ? messages.filter(m => m.status === 'scheduled')
      : messages.filter(m => m.status === 'failed');

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Typography>Loading messages...</Typography></Box>;
  }

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Sent Messages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {messages.length} message{messages.length !== 1 ? 's' : ''} 
            {propertyId && unitId ? ' (filtered by property/unit)' : ' (all properties)'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportToCSV}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="All Messages" />
        <Tab label="Scheduled" />
        <Tab label="Failed" />
      </Tabs>

      {/* Filter Controls */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3, 
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <TextField
          placeholder="Search messages..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 200, flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={e => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Messages Grid */}
      <Grid container spacing={3}>
        {tabMessages.length === 0 ? (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <SendIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No messages found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {propertyId && unitId 
                      ? 'No messages for this property/unit. Clear the filter to see all messages.' 
                      : 'No messages have been sent yet. Select a property/unit to compose your first message.'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          tabMessages.filter((msg, index, self) =>
            index === self.findIndex(m =>
              m.subject === msg.subject &&
              m.body === msg.body &&
              m.createdAt._seconds === msg.createdAt._seconds
            )).map((msg, idx) => (
            <Grid item xs={12} md={6} lg={4} key={idx}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(msg.status)}
                      <Typography variant="h6" noWrap sx={{ maxWidth: 200 }}>
                        {msg.subject}
                      </Typography>
                    </Box>
                    <Box>
                      {activeTab === 1 && (
                        <Tooltip title="Edit Message">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEdit(msg)}
                            aria-label="Edit message"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Message">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDelete(msg)}
                          aria-label="Delete message"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {msg.body?.substring(0, 100)}...
                  </Typography>
                  
                  {/* Property/Unit Display (when viewing all messages) */}
                  {!propertyId && !unitId && (msg.propertyName || msg.unitName) && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                        📍 {msg.propertyName || 'Unknown Property'} - {msg.unitName || 'Unknown Unit'}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Recipients Display */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                      Recipients:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {msg.recipients && msg.recipients.length > 0 ? (
                        msg.recipients.map((recipient, index) => (
                          <Chip
                            key={index}
                            label={recipient}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))
                      ) : msg.recipientId ? (
                        <Chip
                          label={msg.recipientId}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No recipients specified
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={msg.status} 
                      color={getStatusColor(msg.status)} 
                      size="small" 
                    />
                    <Chip 
                      label={`${msg.recipients?.length || 1} recipients`} 
                      size="small" 
                      variant="outlined"
                    />
                    {msg.tag && (
                      <Chip 
                        label={msg.tag} 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    {new Date(msg.createdAt._seconds * 1000).toLocaleString()}
                  </Typography>
                  
                  {activeTab === 2 && msg.errorReason && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                      <strong>Error:</strong> {msg.errorReason}
                    </Typography>
                  )}
                </CardContent>
                
                <CardActions>
                  {activeTab === 1 && (
                    <>
                      <Button 
                        size="small" 
                        onClick={() => handleEdit(msg)}
                        startIcon={<EditIcon />}
                      >
                        Edit
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleCancel(msg)}
                        color="error"
                        startIcon={<CancelIcon />}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {activeTab === 2 && (
                    <Button 
                      size="small" 
                      onClick={() => handleRetry(msg)}
                      color="warning"
                      startIcon={<ReplayIcon />}
                    >
                      Retry
                    </Button>
                  )}
                  <Button 
                    size="small"
                    onClick={() => {
                      setConversationTenant(msg.recipients?.[0] || msg.recipientId);
                      setConversationPropertyId(msg.propertyId || propertyId);
                      setConversationUnitId(msg.unitId || unitId);
                      setConversationOpen(true);
                    }}
                    variant="outlined"
                  >
                    View Conversation
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Scheduled Message</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Subject"
                value={editSubject}
                onChange={e => setEditSubject(e.target.value)}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Message Body"
                value={editBody}
                onChange={e => setEditBody(e.target.value)}
                fullWidth
                margin="normal"
                multiline
                rows={6}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Scheduled At"
                type="datetime-local"
                value={editScheduledAt}
                onChange={e => setEditScheduledAt(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={editLoading}>Update Message</Button>
        </DialogActions>
      </Dialog>

      <ConversationModal
        open={conversationOpen}
        onClose={() => setConversationOpen(false)}
        propertyId={conversationPropertyId || propertyId}
        unitId={conversationUnitId || unitId}
        tenantEmail={conversationTenant}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SentMessages;
